// Admin › Settings › Referral Program › Cost — what the rewards program cost
// each New York month against the entry fees it brought in (rewards part 6),
// plus the referrers who earned the most.

import { useQuery } from '@tanstack/react-query'
import { costReportKey, fetchRewardsCostReport } from '../../../api/referralRewards'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import { useT } from '../../../i18n'
import { rewardsCostI18n } from '../../../i18n/admin/rewards-referrals'
import { formatCurrency } from '../../../lib/currency'
import { formatDate } from '../../../lib/datetime'

function usd(value: string | number): string {
  return formatCurrency(Number(value), 'USD')
}

function monthLabel(month: string): string {
  // Mid-month noon UTC stays inside the same month in every time zone.
  return formatDate(`${month}-15T12:00:00Z`, { year: 'numeric', month: 'short' })
}

export function RewardsCostPage() {
  const t = useT(rewardsCostI18n)
  const query = useQuery({ queryKey: costReportKey, queryFn: fetchRewardsCostReport })
  const months = query.data?.months ?? []
  const top = query.data?.top_referrers ?? []

  return (
    <div className="space-y-4">
      <Card className="space-y-3 p-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h2>
          <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">{t.subtitle}</p>
        </div>
        {query.isLoading ? (
          <p className="text-sm text-[var(--color-text-muted)]">{t.loading}</p>
        ) : query.isError ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span role="alert" className="text-[var(--color-danger)]">
              {t.loadError}
            </span>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              {t.retry}
            </Button>
          </div>
        ) : (
          <>
            <DataTableShell variant="embedded">
              <table>
                <thead>
                  <tr>
                    <th>{t.columns.month}</th>
                    <th className="text-right">{t.columns.fees}</th>
                    <th className="text-right">{t.columns.granted}</th>
                    <th className="text-right">{t.columns.takenBack}</th>
                    <th className="text-right">{t.columns.cash}</th>
                    <th className="text-right">{t.columns.converted}</th>
                    <th className="text-right">{t.columns.net}</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => {
                    const net = Number(m.net_cost_usd)
                    return (
                      <tr key={m.month}>
                        <td className="font-medium">{monthLabel(m.month)}</td>
                        <td className="text-right tabular-nums">{usd(m.entry_fee_income_usd)}</td>
                        <td className="text-right tabular-nums">{usd(m.credit_granted_usd)}</td>
                        <td className="text-right tabular-nums">{usd(m.credit_taken_back_usd)}</td>
                        <td className="text-right tabular-nums">{usd(m.cash_commission_usd)}</td>
                        <td className="text-right tabular-nums text-[var(--color-text-muted)]">
                          {usd(m.converted_usd)}
                        </td>
                        <td
                          className={`text-right font-semibold tabular-nums ${
                            net > 0
                              ? 'text-[var(--color-danger)]'
                              : net < 0
                                ? 'text-[var(--color-success)]'
                                : 'text-[var(--color-text-muted)]'
                          }`}
                        >
                          {usd(m.net_cost_usd)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </DataTableShell>
            <div className="space-y-1 text-xs text-[var(--color-text-muted)]">
              <p>{t.netHint}</p>
              <p>{t.convertedHint}</p>
            </div>
          </>
        )}
      </Card>
      {query.isSuccess && (
        <Card className="space-y-3 p-4">
          <h3 className="text-sm font-semibold text-[var(--color-text)]">{t.topTitle}</h3>
          {top.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t.topEmpty}</p>
          ) : (
            <DataTableShell variant="embedded">
              <table>
                <thead>
                  <tr>
                    <th>{t.topColumns.referrer}</th>
                    <th className="text-right">{t.topColumns.earned}</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((r) => (
                    <tr key={r.user_id}>
                      <td className="font-medium">{r.email ?? t.unknownReferrer}</td>
                      <td className="text-right tabular-nums">{usd(r.earned_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataTableShell>
          )}
        </Card>
      )}
    </div>
  )
}
